import { useState, useEffect } from 'react';
import { Table, Button, message, Tag, Modal, Input, Breadcrumb, Tabs, Select, Space, Card, Statistic, Row, Col, Image } from 'antd';
import { HomeOutlined, ToolOutlined, CheckOutlined, SearchOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import api from '../api';

const { TextArea } = Input;

const RepairManagementPage = () => {
  const [userRole, setUserRole] = useState(null);
  const [allRepairs, setAllRepairs] = useState([]);
  const [pendingRepairs, setPendingRepairs] = useState([]);
  const [myRepairs, setMyRepairs] = useState([]);
  const [myPendingRepairs, setMyPendingRepairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedHours, setSelectedHours] = useState(24);
  const [assigning, setAssigning] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectRemark, setRejectRemark] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [completeRemark, setCompleteRemark] = useState('');
  const [completing, setCompleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  // 批量分配相关
  const [pendingTypeFilter, setPendingTypeFilter] = useState(null);
  const [pendingBuildingFilter, setPendingBuildingFilter] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchAssignModalVisible, setBatchAssignModalVisible] = useState(false);
  const [batchAssigning, setBatchAssigning] = useState(false);

  // 获取用户角色
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await api.get('/auth/me');
        setUserRole(response.data.role);
      } catch (error) {
        console.error('获取用户信息失败');
      }
    };
    fetchUserRole();
  }, []);

  // 根据角色加载数据
  useEffect(() => {
    if (!userRole) return;
    
    if (userRole === 'ADMIN' || userRole === 'LOGISTICS_ADMIN') {
      fetchAllRepairs();
      fetchPendingRepairs();
      fetchStatistics();
      fetchTeachers();
    } else if (userRole === 'LOGISTICS_TEACHER') {
      fetchMyRepairs();
      fetchMyPendingRepairs();
    }
  }, [userRole]);

  const fetchAllRepairs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/repair/all');
      setAllRepairs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('获取报修记录失败');
    }
    setLoading(false);
  };

  const fetchPendingRepairs = async () => {
    try {
      const response = await api.get('/repair/pending');
      setPendingRepairs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('获取待分配报修失败');
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/repair/statistics');
      setStatistics(response.data || {});
    } catch (error) {
      console.error('获取统计数据失败');
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await api.get('/repair/teachers');
      setTeachers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('获取后勤老师列表失败');
    }
  };

  // 后勤生活老师 - 获取我的工单
  const fetchMyRepairs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/repair/my-assigned');
      setMyRepairs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error('获取工单失败');
    }
    setLoading(false);
  };

  const fetchMyPendingRepairs = async () => {
    try {
      const response = await api.get('/repair/my-pending');
      setMyPendingRepairs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('获取待处理工单失败');
    }
  };

  const showAssignModal = (record) => {
    setSelectedRepair(record);
    setSelectedTeacher(null);
    setSelectedHours(24);
    setAssignModalVisible(true);
  };

  const handleAssign = async () => {
    if (!selectedTeacher) {
      message.warning('请选择后勤生活老师');
      return;
    }
    setAssigning(true);
    try {
      await api.post(`/repair/${selectedRepair.id}/assign`, { teacherId: selectedTeacher, hours: selectedHours });
      message.success('分配成功');
      setAssignModalVisible(false);
      fetchAllRepairs();
      fetchPendingRepairs();
      fetchStatistics();
    } catch (error) {
      message.error('分配失败');
    }
    setAssigning(false);
  };

  // 批量分配
  const handleBatchAssign = async () => {
    if (!selectedTeacher) {
      message.warning('请选择后勤生活老师');
      return;
    }
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要分配的工单');
      return;
    }
    setBatchAssigning(true);
    try {
      let successCount = 0;
      for (const id of selectedRowKeys) {
        try {
          await api.post(`/repair/${id}/assign`, { teacherId: selectedTeacher, hours: selectedHours });
          successCount++;
        } catch (e) {
          console.error(`分配工单${id}失败`, e);
        }
      }
      message.success(`成功分配 ${successCount} 个工单`);
      setBatchAssignModalVisible(false);
      setSelectedRowKeys([]);
      setSelectedTeacher(null);
      fetchAllRepairs();
      fetchPendingRepairs();
      fetchStatistics();
    } catch (error) {
      message.error('批量分配失败');
    }
    setBatchAssigning(false);
  };

  const showRejectModal = (record) => {
    setSelectedRepair(record);
    setRejectRemark('');
    setRejectModalVisible(true);
  };

  const handleReject = async () => {
    if (!rejectRemark.trim()) {
      message.warning('请填写拒绝原因');
      return;
    }
    setRejecting(true);
    try {
      await api.post(`/repair/${selectedRepair.id}/reject`, { remark: rejectRemark });
      message.success('已拒绝');
      setRejectModalVisible(false);
      fetchAllRepairs();
      fetchPendingRepairs();
      fetchStatistics();
    } catch (error) {
      message.error('操作失败');
    }
    setRejecting(false);
  };

  const showDetail = (record) => {
    setDetailRecord(record);
    setDetailModalVisible(true);
  };

  // 后勤生活老师 - 开始处理
  const handleStartProcessing = async (record) => {
    try {
      await api.post(`/repair/${record.id}/start`);
      message.success('开始处理');
      fetchMyRepairs();
      fetchMyPendingRepairs();
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 后勤生活老师 - 完成
  const showCompleteModal = (record) => {
    setSelectedRepair(record);
    setCompleteRemark('');
    setCompleteModalVisible(true);
  };

  const handleComplete = async () => {
    if (!completeRemark.trim()) {
      message.warning('请填写处理说明');
      return;
    }
    setCompleting(true);
    try {
      await api.post(`/repair/${selectedRepair.id}/complete`, { 
        remark: completeRemark,
        handleImages: ''
      });
      message.success('已完成');
      setCompleteModalVisible(false);
      fetchMyRepairs();
      fetchMyPendingRepairs();
    } catch (error) {
      message.error('操作失败');
    }
    setCompleting(false);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'PENDING': { color: 'orange', text: '待分配' },
      'ASSIGNED': { color: 'blue', text: '已分配' },
      'PROCESSING': { color: 'purple', text: '处理中' },
      'COMPLETED': { color: 'green', text: '已完成' },
      'REJECTED': { color: 'red', text: '已拒绝' },
      'CANCELLED': { color: 'default', text: '已取消' },
    };
    const s = statusMap[status] || { color: 'default', text: status };
    return <Tag color={s.color}>{s.text}</Tag>;
  };

  const getTypeText = (type) => {
    const typeMap = {
      'ELECTRICAL': '电器故障',
      'PLUMBING': '水管问题',
      'FURNITURE': '家具损坏',
      'DOOR_LOCK': '门锁问题',
      'AIR_CONDITION': '空调问题',
      'NETWORK': '网络问题',
      'LIGHTING': '照明问题',
      'OTHER': '其他',
    };
    return typeMap[type] || type;
  };

  const formatDateTime = (dt) => {
    if (!dt) return '-';
    return dt.replace('T', ' ').split('.')[0];
  };

  // 筛选数据
  const getFilteredData = (data) => {
    let filtered = data;
    if (searchText) {
      const keyword = searchText.toLowerCase();
      filtered = filtered.filter(r => 
        (r.studentName || '').toLowerCase().includes(keyword) ||
        (r.building || '').toLowerCase().includes(keyword) ||
        (r.room || '').toLowerCase().includes(keyword)
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    if (typeFilter) {
      filtered = filtered.filter(r => r.repairType === typeFilter);
    }
    return filtered;
  };

  // 筛选待分配数据
  const getFilteredPendingData = () => {
    let filtered = pendingRepairs;
    if (pendingTypeFilter) {
      filtered = filtered.filter(r => r.repairType === pendingTypeFilter);
    }
    if (pendingBuildingFilter) {
      filtered = filtered.filter(r => r.building === pendingBuildingFilter);
    }
    return filtered;
  };

  // 后勤管理员的列
  const adminColumns = [
    { title: '报修人', dataIndex: 'studentName', key: 'studentName', width: 80 },
    { title: '班级', dataIndex: 'studentClass', key: 'studentClass', width: 100 },
    { title: '类型', dataIndex: 'repairType', key: 'repairType', width: 90, render: (type) => getTypeText(type) },
    { title: '位置', key: 'location', width: 120, render: (_, record) => `${record.building || ''} ${record.room || ''}` },
    { title: '问题描述', dataIndex: 'description', key: 'description', ellipsis: true, width: 150 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 90, render: (status) => getStatusTag(status) },
    { title: '处理人', dataIndex: 'assignedToName', key: 'assignedToName', width: 80, render: (text) => text || '-' },
    { title: '提交时间', dataIndex: 'createdAt', key: 'createdAt', width: 150, render: (dt) => formatDateTime(dt) },
    {
      title: '操作', key: 'action', width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => showDetail(record)}>详情</Button>
          {record.status === 'PENDING' && (
            <>
              <Button type="link" size="small" onClick={() => showAssignModal(record)}>分配</Button>
              <Button type="link" size="small" danger onClick={() => showRejectModal(record)}>拒绝</Button>
            </>
          )}
          {(record.status === 'ASSIGNED' || record.status === 'PROCESSING') && (
            <Button type="link" size="small" onClick={() => showAssignModal(record)}>更换</Button>
          )}
        </Space>
      ),
    },
  ];

  const pendingColumns = [
    ...adminColumns.slice(0, -1),
    {
      title: '操作', key: 'action', width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button type="primary" size="small" onClick={() => showAssignModal(record)}>分配</Button>
          <Button size="small" danger onClick={() => showRejectModal(record)}>拒绝</Button>
        </Space>
      ),
    },
  ];

  // 后勤生活老师的列
  const teacherColumns = [
    { title: '报修人', dataIndex: 'studentName', key: 'studentName', width: 80 },
    { title: '电话', dataIndex: 'studentPhone', key: 'studentPhone', width: 120 },
    { title: '类型', dataIndex: 'repairType', key: 'repairType', width: 90, render: (type) => getTypeText(type) },
    { title: '位置', key: 'location', width: 150, render: (_, record) => `${record.building || ''} ${record.room || ''} ${record.locationDetail || ''}` },
    { title: '问题描述', dataIndex: 'description', key: 'description', ellipsis: true, width: 150 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 90, render: (status) => getStatusTag(status) },
    { title: '截止时间', dataIndex: 'deadline', key: 'deadline', width: 150, render: (dt, record) => (
      <span style={{ color: record.isOverdue ? '#ff4d4f' : undefined }}>
        {formatDateTime(dt)} {record.isOverdue && <Tag color="red">超时</Tag>}
      </span>
    )},
    {
      title: '操作', key: 'action', width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => showDetail(record)}>详情</Button>
          {record.status === 'ASSIGNED' && (
            <Button type="link" size="small" onClick={() => handleStartProcessing(record)}>开始处理</Button>
          )}
          {(record.status === 'ASSIGNED' || record.status === 'PROCESSING') && (
            <Button type="primary" size="small" onClick={() => showCompleteModal(record)}>完成</Button>
          )}
        </Space>
      ),
    },
  ];

  // 后勤管理员的Tab
  const adminTabs = [
    {
      key: 'pending',
      label: `待分配 (${pendingRepairs.length})`,
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Select placeholder="类型筛选" allowClear style={{ width: 120 }} value={pendingTypeFilter} onChange={setPendingTypeFilter}
              options={[
                { label: '电器故障', value: 'ELECTRICAL' }, { label: '水管问题', value: 'PLUMBING' },
                { label: '家具损坏', value: 'FURNITURE' }, { label: '门锁问题', value: 'DOOR_LOCK' },
                { label: '空调问题', value: 'AIR_CONDITION' }, { label: '网络问题', value: 'NETWORK' },
                { label: '照明问题', value: 'LIGHTING' }, { label: '其他', value: 'OTHER' },
              ]} />
            <Select placeholder="楼栋筛选" allowClear style={{ width: 120 }} value={pendingBuildingFilter} onChange={setPendingBuildingFilter}
              options={[
                { label: 'A楼', value: 'A楼' },
                { label: 'B楼', value: 'B楼' },
                { label: 'C楼', value: 'C楼' },
                { label: 'D楼', value: 'D楼' },
              ]} />
            <div style={{ flex: 1 }} />
            {selectedRowKeys.length > 0 && (
              <span style={{ color: '#1890ff' }}>已选择 {selectedRowKeys.length} 项</span>
            )}
            <Button type="primary" disabled={selectedRowKeys.length === 0} onClick={() => {
              setSelectedTeacher(null);
              setSelectedHours(24);
              setBatchAssignModalVisible(true);
            }}>批量分配</Button>
          </div>
          <Table 
            columns={pendingColumns} 
            dataSource={getFilteredPendingData()} 
            loading={loading} 
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            pagination={{ defaultPageSize: 10, showTotal: (total) => `共 ${total} 条` }} 
          />
        </>
      ),
    },
    {
      key: 'all',
      label: '全部记录',
      children: (
        <>
          <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Space.Compact>
              <Input
                placeholder="搜索报修人/位置"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={() => setSearchText(searchText)}
                style={{ width: 140 }}
                prefix={<SearchOutlined />}
              />
              <Button aria-label="搜索报修" icon={<SearchOutlined />} onClick={() => setSearchText(searchText)} />
            </Space.Compact>
            <Select placeholder="状态筛选" allowClear style={{ width: 120 }} value={statusFilter} onChange={setStatusFilter}
              options={[
                { label: '待分配', value: 'PENDING' }, { label: '已分配', value: 'ASSIGNED' },
                { label: '处理中', value: 'PROCESSING' }, { label: '已完成', value: 'COMPLETED' }, { label: '已拒绝', value: 'REJECTED' },
              ]} />
            <Select placeholder="类型筛选" allowClear style={{ width: 120 }} value={typeFilter} onChange={setTypeFilter}
              options={[
                { label: '电器故障', value: 'ELECTRICAL' }, { label: '水管问题', value: 'PLUMBING' },
                { label: '家具损坏', value: 'FURNITURE' }, { label: '门锁问题', value: 'DOOR_LOCK' },
                { label: '空调问题', value: 'AIR_CONDITION' }, { label: '网络问题', value: 'NETWORK' },
                { label: '照明问题', value: 'LIGHTING' }, { label: '其他', value: 'OTHER' },
              ]} />
          </div>
          <Table columns={adminColumns} dataSource={getFilteredData(allRepairs)} loading={loading} rowKey="id"
            pagination={{ defaultPageSize: 10, showTotal: (total) => `共 ${total} 条` }} />
        </>
      ),
    },
  ];

  // 后勤生活老师的Tab
  const teacherTabs = [
    {
      key: 'pending',
      label: `待处理 (${myPendingRepairs.length})`,
      children: (
        <Table columns={teacherColumns} dataSource={myPendingRepairs} loading={loading} rowKey="id"
          pagination={{ defaultPageSize: 10, showTotal: (total) => `共 ${total} 条` }} />
      ),
    },
    {
      key: 'all',
      label: '全部工单',
      children: (
        <Table columns={teacherColumns} dataSource={myRepairs} loading={loading} rowKey="id"
          pagination={{ defaultPageSize: 10, showTotal: (total) => `共 ${total} 条` }} />
      ),
    },
  ];

  if (!userRole) {
    return <div>加载中...</div>;
  }

  const isAdmin = userRole === 'ADMIN' || userRole === 'LOGISTICS_ADMIN';

  return (
    <div>
      <div className="page-header">
        <Breadcrumb items={[{ title: <><HomeOutlined /> 首页</> }, { title: '报修管理' }]} />
        <h2 className="page-title">{isAdmin ? '报修管理' : '我的工单'}</h2>
      </div>

      {/* 统计卡片 - 仅管理员显示 */}
      {isAdmin && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card><Statistic title="待分配" value={statistics.pending || 0} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#faad14' }} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card><Statistic title="处理中" value={(statistics.assigned || 0) + (statistics.processing || 0)} prefix={<ToolOutlined />} valueStyle={{ color: '#1890ff' }} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card><Statistic title="已完成" value={statistics.completed || 0} prefix={<CheckOutlined />} valueStyle={{ color: '#52c41a' }} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card><Statistic title="总计" value={statistics.total || 0} prefix={<ExclamationCircleOutlined />} /></Card>
          </Col>
        </Row>
      )}

      <div className="table-card">
        <Tabs defaultActiveKey="pending" items={isAdmin ? adminTabs : teacherTabs} />
      </div>

      {/* 分配弹窗 */}
      <Modal title="分配报修工单" open={assignModalVisible} onCancel={() => setAssignModalVisible(false)}
        onOk={handleAssign} confirmLoading={assigning} okText="确认分配">
        <div style={{ marginBottom: 16 }}>
          <p><strong>报修类型：</strong>{selectedRepair && getTypeText(selectedRepair.repairType)}</p>
          <p><strong>位置：</strong>{selectedRepair && `${selectedRepair.building} ${selectedRepair.room}`}</p>
          <p><strong>问题描述：</strong>{selectedRepair?.description}</p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 8 }}><strong>选择后勤生活老师：</strong></p>
          <Select style={{ width: '100%' }} placeholder="请选择" value={selectedTeacher} onChange={setSelectedTeacher}
            options={teachers.map(t => ({ label: `${t.name} (待处理: ${t.pendingCount || 0})`, value: t.id }))} />
        </div>
        <div>
          <p style={{ marginBottom: 8 }}><strong>维修时限（小时）：</strong></p>
          <Select style={{ width: '100%' }} value={selectedHours} onChange={setSelectedHours}
            options={[
              { label: '2小时', value: 2 },
              { label: '4小时', value: 4 },
              { label: '8小时', value: 8 },
              { label: '12小时', value: 12 },
              { label: '24小时', value: 24 },
              { label: '48小时', value: 48 },
              { label: '72小时', value: 72 },
            ]} />
        </div>
      </Modal>

      {/* 批量分配弹窗 */}
      <Modal title="批量分配工单" open={batchAssignModalVisible} onCancel={() => setBatchAssignModalVisible(false)}
        onOk={handleBatchAssign} confirmLoading={batchAssigning} okText="确认分配">
        <div style={{ marginBottom: 16 }}>
          <p><strong>已选择 {selectedRowKeys.length} 个工单</strong></p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 8 }}><strong>选择后勤生活老师：</strong></p>
          <Select style={{ width: '100%' }} placeholder="请选择" value={selectedTeacher} onChange={setSelectedTeacher}
            options={teachers.map(t => ({ label: `${t.name} (待处理: ${t.pendingCount || 0})`, value: t.id }))} />
        </div>
        <div>
          <p style={{ marginBottom: 8 }}><strong>维修时限（小时）：</strong></p>
          <Select style={{ width: '100%' }} value={selectedHours} onChange={setSelectedHours}
            options={[
              { label: '2小时', value: 2 },
              { label: '4小时', value: 4 },
              { label: '8小时', value: 8 },
              { label: '12小时', value: 12 },
              { label: '24小时', value: 24 },
              { label: '48小时', value: 48 },
              { label: '72小时', value: 72 },
            ]} />
        </div>
      </Modal>

      {/* 拒绝弹窗 */}
      <Modal title="拒绝报修" open={rejectModalVisible} onCancel={() => setRejectModalVisible(false)}
        onOk={handleReject} confirmLoading={rejecting} okText="确认拒绝" okButtonProps={{ danger: true }}>
        <p>请填写拒绝原因：</p>
        <TextArea rows={3} value={rejectRemark} onChange={(e) => setRejectRemark(e.target.value)} placeholder="请输入拒绝原因" />
      </Modal>

      {/* 完成弹窗 */}
      <Modal title="完成报修" open={completeModalVisible} onCancel={() => setCompleteModalVisible(false)}
        onOk={handleComplete} confirmLoading={completing} okText="确认完成">
        <p>请填写处理说明：</p>
        <TextArea rows={3} value={completeRemark} onChange={(e) => setCompleteRemark(e.target.value)} placeholder="请描述处理情况" />
      </Modal>

      {/* 详情弹窗 */}
      <Modal title="报修详情" open={detailModalVisible} onCancel={() => setDetailModalVisible(false)}
        footer={<Button onClick={() => setDetailModalVisible(false)}>关闭</Button>} width={600}>
        {detailRecord && (
          <div>
            <Row gutter={[16, 12]}>
              <Col span={12}><strong>报修人：</strong>{detailRecord.studentName}</Col>
              <Col span={12}><strong>班级：</strong>{detailRecord.studentClass || '-'}</Col>
              <Col span={12}><strong>联系电话：</strong>{detailRecord.studentPhone || '-'}</Col>
              <Col span={12}><strong>报修类型：</strong>{getTypeText(detailRecord.repairType)}</Col>
              <Col span={24}><strong>位置：</strong>{detailRecord.building} {detailRecord.room} {detailRecord.locationDetail}</Col>
              <Col span={24}><strong>问题描述：</strong>{detailRecord.description}</Col>
              <Col span={12}><strong>状态：</strong>{getStatusTag(detailRecord.status)}</Col>
              <Col span={12}><strong>提交时间：</strong>{formatDateTime(detailRecord.createdAt)}</Col>
              {detailRecord.assignedToName && (
                <>
                  <Col span={12}><strong>处理人：</strong>{detailRecord.assignedToName}</Col>
                  <Col span={12}><strong>分配时间：</strong>{formatDateTime(detailRecord.assignedAt)}</Col>
                  <Col span={12}><strong>截止时间：</strong>{formatDateTime(detailRecord.deadline)}</Col>
                  {detailRecord.isOverdue && <Col span={12}><Tag color="red">已超时</Tag></Col>}
                </>
              )}
              {detailRecord.completedAt && (<Col span={12}><strong>完成时间：</strong>{formatDateTime(detailRecord.completedAt)}</Col>)}
              {detailRecord.handleRemark && (<Col span={24}><strong>处理备注：</strong>{detailRecord.handleRemark}</Col>)}
            </Row>
            {detailRecord.images && (
              <div style={{ marginTop: 16 }}>
                <strong>报修图片：</strong>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <Image.PreviewGroup>
                    {detailRecord.images.split(',').filter(Boolean).map((img, idx) => (
                      <Image key={idx} width={100} height={100} src={img} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    ))}
                  </Image.PreviewGroup>
                </div>
              </div>
            )}
            {detailRecord.handleImages && (
              <div style={{ marginTop: 16 }}>
                <strong>处理图片：</strong>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <Image.PreviewGroup>
                    {detailRecord.handleImages.split(',').filter(Boolean).map((img, idx) => (
                      <Image key={idx} width={100} height={100} src={img} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    ))}
                  </Image.PreviewGroup>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RepairManagementPage;
